import { useCallback, useMemo } from 'react';
import {
  FEATURE_HIDE_TRACKS,
  FEATURE_LOCK_TRACKS,
  FEATURE_MAGNETIC_TRACKS,
  FEATURE_MUTE_TRACKS,
} from '../../flags';
import { setSelectedItems } from '../../state/actions/set-selected-items';
import {
  useAllItems,
  useLinkingEnabled,
  useSelectedItems,
  useWriteContext,
} from '../../utils/use-context';
import { TimelineTrackAndLayout } from '../utils/drag/calculate-track-heights';
import { TimelineHideTrack } from './timeline-hide-track';
import { TimelineLockTrack } from './timeline-lock-track';
import { TimelineMagneticTrack } from './timeline-magnetic-track';
import { TimelineMuteTrack } from './timeline-mute-track';

const isLinkedTrack = (
  trackAndLayout: TimelineTrackAndLayout,
  isLinkingEnabled: boolean,
): boolean => {
  if (!isLinkingEnabled) {
    return false;
  }

  const { track } = trackAndLayout;
  const isMainTrack = track.metadata?.isMainTrack === true;
  const trackType = track.metadata?.type;

  return (
    isMainTrack &&
    (trackType === 'video' || trackType === 'caption' || trackType === 'audio')
  );
};

export const TrackHeader = ({
  trackAndLayout,
  trackIndex,
  zoneLabel,
}: {
  trackAndLayout: TimelineTrackAndLayout;
  trackIndex: number;
  zoneLabel: string;
}) => {
  const isLinkingEnabled = useLinkingEnabled();
  const { items } = useAllItems();
  const { setState } = useWriteContext();
  const { selectedItems } = useSelectedItems();

  const isLinked = isLinkedTrack(trackAndLayout, isLinkingEnabled);
  const isEvenTrack = trackIndex % 2 === 0;
  const isMainTrack =
    (trackAndLayout.track.metadata?.isMainTrack === true ||
      trackAndLayout.track.metadata?.trackType === 'main') &&
    (trackAndLayout.track.metadata?.type === 'video' ||
      trackAndLayout.track.metadata?.type === 'caption' ||
      trackAndLayout.track.metadata?.type === 'audio');

  const trackItems = useMemo(() => {
    return trackAndLayout.track.items
      .map((itemId) => items[itemId])
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [items, trackAndLayout.track.items]);

  const hasSoundControllableItem = useMemo(() => {
    return (
      trackAndLayout.track.metadata?.type === 'audio' ||
      trackAndLayout.track.metadata?.type === 'video' ||
      trackItems.some((item) => item.type === 'audio' || item.type === 'video')
    );
  }, [trackAndLayout.track.metadata?.type, trackItems]);

  const hasVisualItem = useMemo(() => {
    return (
      (trackAndLayout.track.metadata?.type !== undefined &&
        trackAndLayout.track.metadata.type !== 'audio') ||
      trackItems.some((item) => item.type !== 'audio')
    );
  }, [trackAndLayout.track.metadata?.type, trackItems]);

  const isAudioOnlyTrack =
    (trackAndLayout.track.metadata?.type === 'audio' ||
      trackItems.some((item) => item.type === 'audio')) &&
    !hasVisualItem;

  const style = useMemo(() => {
    return {
      height: trackAndLayout.height,
      boxShadow: isLinked
        ? 'inset 0 0 0 1px var(--color-ref-extended-blue-500)'
        : undefined,
    };
  }, [trackAndLayout, isLinked]);

  const isLinkedMain = isMainTrack && isLinkingEnabled;

  const bgClass = isLinkedMain
    ? 'bg-sys-bg-surface-primary hover:brightness-110'
    : isEvenTrack
      ? 'bg-sys-bg-surface hover:bg-sys-bg-surface-1-hover'
      : 'bg-sys-bg-surface-2 hover:brightness-120';

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const trackItemIds = trackAndLayout.track.items;
      const isAdditive = e.shiftKey;

      setState({
        update: (state) => {
          const currentSelected = state.selectedItems;
          const newSelection = isAdditive
            ? [
                ...currentSelected.filter((id) => !trackItemIds.includes(id)),
                ...trackItemIds,
              ]
            : trackItemIds;
          return setSelectedItems(state, newSelection);
        },
        commitToUndoStack: false,
      });
    },
    [trackAndLayout.track.items, setState],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={`group relative flex w-full shrink-0 cursor-pointer items-center gap-2 py-2xs pr-xs pl-md text-xs transition-colors ${bgClass}`}
      style={style}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
    >
      <div className="flex h-5 shrink-0 items-center rounded-2xs bg-sys-style-neutral-light px-3xs">
        <span className="text-xs leading-none font-bold text-sys-cont-neutral-default">
          {zoneLabel}
        </span>
      </div>
      {isMainTrack ? (
        <div
          className={`flex h-5 shrink-0 items-center rounded-2xs px-3xs ${isLinkingEnabled ? 'bg-sys-style-primary-default' : 'bg-sys-style-neutral-light'}`}
        >
          <span
            className={`text-xs leading-none font-bold ${isLinkingEnabled ? 'text-sys-cont-none-light' : 'text-sys-cont-neutral-default'}`}
          >
            M
          </span>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 items-center justify-end gap-0.5">
        {FEATURE_LOCK_TRACKS && (
          <TimelineLockTrack track={trackAndLayout.track} />
        )}
        {FEATURE_HIDE_TRACKS && !isAudioOnlyTrack && (
          <TimelineHideTrack track={trackAndLayout.track} />
        )}
        {FEATURE_MUTE_TRACKS && hasSoundControllableItem && (
          <TimelineMuteTrack track={trackAndLayout.track} />
        )}
        {FEATURE_MAGNETIC_TRACKS && (
          <TimelineMagneticTrack track={trackAndLayout.track} />
        )}
      </div>
    </div>
  );
};
